import { UpsertDocScanCropField } from "services";

import { DocScanFieldRecord } from "models";

import { getDocFieldCrops, requiresDocFieldSplit } from "components";

// The matched farm field id lives on `fieldId` for auto-matched rows, but on `farmFieldRaw.id` for
// need_review rows (where the top-level `fieldId` is left null until the match is confirmed).
export const getDocScanFieldId = (r: DocScanFieldRecord): string | null => r.fieldId ?? r.farmFieldRaw?.id ?? null;

export const isEligibleForDateEntry = (r: DocScanFieldRecord): boolean => {
  // Must be matched to a known field
  if (!getDocScanFieldId(r)) return false;

  // Parcels with different crops grouped under one field need a split first (not supported yet)
  if (requiresDocFieldSplit(r.docFieldRaw)) return false;

  const docCrops = getDocFieldCrops(r.docFieldRaw);

  // Must have at least one crop from the scanned document
  if (!docCrops.length) return false;

  // Every crop must have a crop_code — entries without one can't be saved
  if (!docCrops.every((c) => !!c.crop_code)) return false;

  // Skip fields that already have crops saved — date entry is only for new data
  if (r.farmFieldRaw?.crops?.length) return false;

  return true;
};

// Builds one upsert-crops request field from a date-entry row. crop_code is passed through
// unchanged — it's a document-extracted code/identifier (numeric for DE, free text for PL), not a
// number to coerce.
export const buildUpsertDocScanCropField = (
  r: DocScanFieldRecord,
  fieldDates: Map<string, { seedingStart: string; seedingEnd: string; harvestStart: string; harvestEnd: string }>,
  dateEntryYear: number
): UpsertDocScanCropField => {
  const dates = fieldDates.get(r.id);

  return {
    farmDocumentScanFieldId: r.id,
    fieldId: getDocScanFieldId(r)!,
    // need_review crops have no crop_year; fall back to the scan reporting year (always set on date entry) so upsert always gets one.
    crops: getDocFieldCrops(r.docFieldRaw).map((c) => ({ ...c, crop_year: c.crop_year ?? dateEntryYear })),
    seedingStart: dates?.seedingStart ?? "",
    seedingEnd: dates?.seedingEnd ?? "",
    harvestStart: dates?.harvestStart ?? "",
    harvestEnd: dates?.harvestEnd ?? "",
  };
};
